import { useState } from "react";
import "./AdminLogin.css";
import PasswordInput from "./PasswordInput";
import logoWhite from "../assets/opsmaticz_logo_white.png";
import logoDark from "../assets/opsmaticz_logo.png";

const Icon = ({ d, size = 18, strokeWidth = 2, className = "" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    email: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'm22 6-10 7L2 6'],
    shield: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
    arrow: ['M5 12h14', 'm12 5 7 7-7 7'],
    alert: ['M12 8v4', 'M12 16h.01', 'M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z'],
    zap: ['M13 2L3 14h9l-1 8 10-12h-9l1-8z'],
    globe: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M2 12h20', 'M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z'],
    truck: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z', 'M18.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    lock: ['M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z', 'M7 11V7a5 5 0 0 1 10 0v4']
};

export default function AdminLogin({ onLogin }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setIsLoading(true);

        try {
            const res = await fetch("/api/login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password, portalType: 'admin' }),
            });

            const data = await res.json();

            if (res.ok) {
                if (data.user.userType !== 'admin') {
                    setError("Not authorized. This portal is for Superadmins only.");
                    setIsLoading(false);
                    return;
                }
                localStorage.setItem("ops-user", JSON.stringify(data.user));
                onLogin(data.user);
            } else {
                setError(data.message || "Invalid credentials. Please try again.");
            }
        } catch (err) {
            setError("Connection failed. Check your server.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            {/* ── Left Hero Visual Side ── */}
            <div className="login-side-decoration admin-theme-glow">
                <div className="hero-blueprint-bg"></div>
                <div className="ambient-orbs">
                    <div className="orb orb-1"></div>
                    <div className="orb orb-2"></div>
                </div>

                <div className="login-brand-header">
                    <div className="brand-logo-wrap">
                        <img src={logoWhite} alt="OpsMaticz Logo" className="brand-logo-img" />
                    </div>
                    <div className="portal-badge-pill">
                        <span className="portal-badge-dot"></span>
                        Superadmin Control
                    </div>
                </div>

                <div className="login-hero-card">
                    <div className="hero-status-pill">
                        <span className="pulse-green"></span>
                        System Operational • Master Oversight
                    </div>
                    <h2>Enterprise Control Tower</h2>
                    <p>Global oversight for platform operators, pricing configurations, system security, and real-time infrastructure metrics.</p>
                    <div className="hero-feature-tags">
                        <span className="hero-tag">
                            <Icon d={icons.shield} size={14} /> Master Control
                        </span>
                        <span className="hero-tag">
                            <Icon d={icons.zap} size={14} /> System Analytics
                        </span>
                        <span className="hero-tag">
                            <Icon d={icons.globe} size={14} /> Multi-Tenant Fleet
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Right Side Form ── */}
            <div className="login-main">
                <div className="login-box">
                    <div className="mobile-brand-bar">
                        <img src={logoDark} alt="OpsMaticz Logo" />
                    </div>

                    <div className="login-box-header">
                        <div className="portal-title-icon">
                            <Icon d={icons.shield} size={24} />
                        </div>
                        <h1>Welcome Back</h1>
                        <p>Sign in to Superadmin Control Panel</p>
                    </div>

                    <form className="login-form-premium" onSubmit={handleSubmit}>
                        {error && (
                            <div className="error-message-shake">
                                <Icon d={icons.alert} size={18} />
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="input-field">
                            <label>Admin Email</label>
                            <div className="input-inner">
                                <Icon d={icons.email} size={18} className="input-icon-svg" />
                                <input
                                    type="email"
                                    placeholder="admin@opsmaticz.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="input-field">
                            <div className="label-row">
                                <label>Password</label>
                            </div>
                            <div className="input-inner">
                                <Icon d={icons.lock} size={18} className="input-icon-svg" />
                                <PasswordInput
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <button type="submit" className="submit-btn" disabled={isLoading}>
                            {isLoading ? (
                                <span className="loader-text">
                                    <span className="dot-flashing"></span>
                                    Verifying...
                                </span>
                            ) : (
                                <>
                                    <span>Enter Admin Panel</span>
                                    <Icon d={icons.arrow} size={18} />
                                </>
                            )}
                        </button>
                    </form>

                    {/* ── Switcher Navigation Footer ── */}
                    <div className="portal-switcher-box">
                        <div className="portal-switcher-label">Switch Portal Access</div>
                        <div className="portal-tabs">
                            <a href="#superadmin-login" className="portal-tab-btn active">
                                <Icon d={icons.shield} size={15} />
                                <span>Admin</span>
                            </a>
                            <a href="#operator-login" className="portal-tab-btn">
                                <Icon d={icons.zap} size={15} />
                                <span>Operator</span>
                            </a>
                            <a href="#vendor-login" className="portal-tab-btn">
                                <Icon d={icons.truck} size={15} />
                                <span>Vendor</span>
                            </a>
                            <a href="#client-login" className="portal-tab-btn">
                                <Icon d={icons.user} size={15} />
                                <span>Client</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
